import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Cpu, MemoryStick, HardDrive, Gauge, Network, Thermometer,
  Clock, TrendingUp, TrendingDown, Activity, RefreshCw,
  ChevronDown, Download, Upload, Zap
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ResourceCharts = () => {
  const [timeRange, setTimeRange] = useState('1h'); // 1h, 6h, 24h, 7d
  const [historyData, setHistoryData] = useState({
    cpu: [],
    memory: [],
    gpu: [],
    network_rx: [],
    network_tx: [],
    disk: []
  });
  const [currentData, setCurrentData] = useState({
    cpu: 0,
    memory: 0,
    gpu: 0,
    gpu_temp: 0,
    network_rx: 0,
    network_tx: 0,
    disk: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const chartRefs = useRef({});

  const timeRangeOptions = [
    { value: '15m', label: '15 min' },
    { value: '1h', label: '1 heure' },
    { value: '6h', label: '6 heures' },
    { value: '24h', label: '24 heures' },
    { value: '7d', label: '7 jours' },
  ];

  const getMaxPoints = () => {
    switch (timeRange) {
      case '15m': return 15;
      case '1h': return 60;
      case '6h': return 72;
      case '24h': return 96;
      case '7d': return 168;
      default: return 60;
    }
  };

  // Generate simulated history data
  const generateHistoryData = useCallback(() => {
    const maxPoints = getMaxPoints();
    const now = Date.now();
    const interval = {
      '15m': 60000, // 1 min
      '1h': 60000,  // 1 min
      '6h': 300000, // 5 min
      '24h': 900000, // 15 min
      '7d': 3600000, // 1 hour
    }[timeRange];

    const generateValues = (base, variance) => {
      return Array.from({ length: maxPoints }, (_, i) => {
        const time = now - (maxPoints - i) * interval;
        // Add some realistic patterns - higher during "work hours"
        const hour = new Date(time).getHours();
        const workHourBoost = (hour >= 9 && hour <= 18) ? variance * 0.3 : 0;
        const value = Math.max(0, Math.min(100,
          base + (Math.random() - 0.5) * variance + workHourBoost + Math.sin(i / 10) * 5
        ));
        return { time, value: Math.round(value * 10) / 10 };
      });
    };

    setHistoryData({
      cpu: generateValues(35, 40),
      memory: generateValues(45, 20),
      gpu: generateValues(25, 35),
      network_rx: generateValues(15, 25),
      network_tx: generateValues(8, 15),
      disk: generateValues(55, 5)
    });
  }, [timeRange]);

  // Fetch current resources
  const fetchCurrentResources = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/system/resources`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentData({
          cpu: data.cpu?.usage || Math.random() * 50 + 20,
          memory: data.memory?.percent || Math.random() * 40 + 30,
          gpu: data.gpu?.utilization || Math.random() * 40 + 10,
          gpu_temp: data.gpu?.temperature || Math.random() * 20 + 45,
          network_rx: data.network?.rx_rate || Math.random() * 30,
          network_tx: data.network?.tx_rate || Math.random() * 15,
          disk: data.disk?.percent || 55
        });
      } else {
        // Simulate data
        setCurrentData({
          cpu: Math.random() * 50 + 20,
          memory: Math.random() * 40 + 30,
          gpu: Math.random() * 40 + 10,
          gpu_temp: Math.random() * 20 + 45,
          network_rx: Math.random() * 30,
          network_tx: Math.random() * 15,
          disk: 55 + Math.random() * 5
        });
      }
    } catch (err) {
      // Simulate data on error
      setCurrentData(prev => ({
        cpu: Math.max(5, Math.min(95, prev.cpu + (Math.random() - 0.5) * 10)),
        memory: Math.max(20, Math.min(90, prev.memory + (Math.random() - 0.5) * 5)),
        gpu: Math.max(0, Math.min(100, prev.gpu + (Math.random() - 0.5) * 15)),
        gpu_temp: Math.max(35, Math.min(85, prev.gpu_temp + (Math.random() - 0.5) * 3)),
        network_rx: Math.max(0, prev.network_rx + (Math.random() - 0.5) * 10),
        network_tx: Math.max(0, prev.network_tx + (Math.random() - 0.5) * 5),
        disk: prev.disk
      }));
    }
  }, []);

  // Update history with current data point
  const updateHistory = useCallback(() => {
    const now = Date.now();
    setHistoryData(prev => ({
      cpu: [...prev.cpu.slice(-getMaxPoints() + 1), { time: now, value: currentData.cpu }],
      memory: [...prev.memory.slice(-getMaxPoints() + 1), { time: now, value: currentData.memory }],
      gpu: [...prev.gpu.slice(-getMaxPoints() + 1), { time: now, value: currentData.gpu }],
      network_rx: [...prev.network_rx.slice(-getMaxPoints() + 1), { time: now, value: currentData.network_rx }],
      network_tx: [...prev.network_tx.slice(-getMaxPoints() + 1), { time: now, value: currentData.network_tx }],
      disk: [...prev.disk.slice(-getMaxPoints() + 1), { time: now, value: currentData.disk }]
    }));
  }, [currentData]);

  useEffect(() => {
    generateHistoryData();
    fetchCurrentResources();
  }, [timeRange]);

  useEffect(() => {
    const fetchInterval = setInterval(fetchCurrentResources, 3000);
    const updateInterval = setInterval(updateHistory, 5000);
    return () => {
      clearInterval(fetchInterval);
      clearInterval(updateInterval);
    };
  }, [fetchCurrentResources, updateHistory]);

  const getColorByValue = (value, thresholds = { good: 50, warning: 70, danger: 85 }) => {
    if (value >= thresholds.danger) return { text: 'text-red-500', bg: 'bg-red-500', stroke: '#ef4444', fill: 'rgba(239, 68, 68, 0.2)' };
    if (value >= thresholds.warning) return { text: 'text-orange-500', bg: 'bg-orange-500', stroke: '#f97316', fill: 'rgba(249, 115, 22, 0.2)' };
    if (value >= thresholds.good) return { text: 'text-yellow-500', bg: 'bg-yellow-500', stroke: '#eab308', fill: 'rgba(234, 179, 8, 0.2)' };
    return { text: 'text-green-500', bg: 'bg-green-500', stroke: '#22c55e', fill: 'rgba(34, 197, 94, 0.2)' };
  };

  // SVG Line Chart Component
  const LineChart = ({ data, color, height = 80, showArea = true, label, unit = '%' }) => {
    if (!data || data.length < 2) return null;

    const width = 300;
    const padding = { top: 10, right: 10, bottom: 20, left: 35 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const values = data.map(d => d.value);
    const minVal = Math.min(...values) * 0.9;
    const maxVal = Math.max(...values) * 1.1 || 100;

    const xScale = (i) => padding.left + (i / (data.length - 1)) * chartWidth;
    const yScale = (v) => padding.top + chartHeight - ((v - minVal) / (maxVal - minVal)) * chartHeight;

    const linePath = data.map((d, i) =>
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.value)}`
    ).join(' ');

    const areaPath = `${linePath} L ${xScale(data.length - 1)} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;

    const lastValue = values[values.length - 1];
    const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
    const trend = values[values.length - 1] - values[Math.max(0, values.length - 10)];

    return (
      <div className="relative">
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(v => {
            const y = yScale(v);
            if (y < padding.top || y > height - padding.bottom) return null;
            return (
              <g key={v}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={width - padding.right}
                  y2={y}
                  stroke="#374151"
                  strokeDasharray="2,2"
                />
                <text x={padding.left - 5} y={y + 3} fill="#6b7280" fontSize="8" textAnchor="end">
                  {v}
                </text>
              </g>
            );
          })}

          {/* Area */}
          {showArea && (
            <path d={areaPath} fill={color.fill} />
          )}

          {/* Line */}
          <path
            d={linePath}
            fill="none"
            stroke={color.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Current value dot */}
          <circle
            cx={xScale(data.length - 1)}
            cy={yScale(lastValue)}
            r="4"
            fill={color.stroke}
          />
          <circle
            cx={xScale(data.length - 1)}
            cy={yScale(lastValue)}
            r="6"
            fill={color.stroke}
            opacity="0.3"
          />
        </svg>

        {/* Stats overlay */}
        <div className="absolute top-1 right-1 text-right">
          <div className={`text-lg font-bold ${color.text}`}>
            {lastValue.toFixed(1)}{unit}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            {trend > 0 ? (
              <TrendingUp className="w-3 h-3 text-red-400" />
            ) : (
              <TrendingDown className="w-3 h-3 text-green-400" />
            )}
            <span>Moy: {avgValue.toFixed(1)}{unit}</span>
          </div>
        </div>
      </div>
    );
  };

  // Bar Chart for histogram
  const BarChart = ({ data, color, height = 100, label }) => {
    if (!data || data.length < 2) return null;

    const width = 300;
    const padding = { top: 10, right: 10, bottom: 25, left: 35 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const barWidth = Math.max(2, (chartWidth / data.length) - 1);
    const maxVal = Math.max(...data.map(d => d.value)) * 1.1 || 100;

    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Y-axis labels */}
        {[0, 50, 100].map(v => {
          const y = padding.top + chartHeight - (v / maxVal) * chartHeight;
          return (
            <text key={v} x={padding.left - 5} y={y + 3} fill="#6b7280" fontSize="8" textAnchor="end">
              {v}
            </text>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const barHeight = (d.value / maxVal) * chartHeight;
          const x = padding.left + (i / data.length) * chartWidth;
          const y = padding.top + chartHeight - barHeight;
          const barColor = getColorByValue(d.value);

          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={barColor.stroke}
              opacity={0.8}
              rx={1}
            />
          );
        })}

        {/* X-axis time labels */}
        {[0, Math.floor(data.length / 2), data.length - 1].map(i => {
          if (!data[i]) return null;
          const x = padding.left + (i / data.length) * chartWidth;
          const time = new Date(data[i].time);
          const label = timeRange === '7d'
            ? time.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
            : time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
          return (
            <text key={i} x={x} y={height - 5} fill="#6b7280" fontSize="8" textAnchor="middle">
              {label}
            </text>
          );
        })}
      </svg>
    );
  };

  // Gauge component
  const GaugeChart = ({ value, label, icon: Icon, color, unit = '%', max = 100 }) => {
    const percentage = Math.min(100, (value / max) * 100);
    const strokeDasharray = `${percentage * 2.51327} 251.327`;
    const gaugeColor = getColorByValue(percentage);

    return (
      <div className="flex flex-col items-center">
        <div className="relative w-24 h-24">
          <svg className="transform -rotate-90 w-24 h-24">
            {/* Background circle */}
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke="#374151"
              strokeWidth="8"
            />
            {/* Value circle */}
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke={gaugeColor.stroke}
              strokeWidth="8"
              strokeDasharray={strokeDasharray}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Icon className={`w-5 h-5 ${gaugeColor.text}`} />
            <span className={`text-lg font-bold ${gaugeColor.text}`}>
              {value.toFixed(1)}{unit}
            </span>
          </div>
        </div>
        <span className="text-xs text-gray-400 mt-1">{label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Time range selector */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-brand-green" />
          <span className="text-white font-medium">Historique des ressources</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-brand-green"
          >
            {timeRangeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => {
              setIsLoading(true);
              generateHistoryData();
              fetchCurrentResources();
              setTimeout(() => setIsLoading(false), 500);
            }}
            className="p-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Gauges row */}
      <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
        <h3 className="text-sm text-gray-400 mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Real-time current status
        </h3>
        <div className="flex justify-around flex-wrap gap-4">
          <GaugeChart value={currentData.cpu} label="CPU" icon={Cpu} />
          <GaugeChart value={currentData.memory} label="RAM" icon={MemoryStick} />
          <GaugeChart value={currentData.gpu} label="GPU" icon={Gauge} />
          <GaugeChart value={currentData.gpu_temp} label="GPU Temp" icon={Thermometer} max={100} unit="°C" />
          <GaugeChart value={currentData.disk} label="Disque" icon={HardDrive} />
        </div>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CPU Chart */}
        <div className="bg-brand-black rounded-xl p-4 border border-green-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Cpu className="w-5 h-5 text-green-400" />
            <span className="text-white font-medium">Charge CPU</span>
          </div>
          <LineChart
            data={historyData.cpu}
            color={getColorByValue(currentData.cpu)}
            height={120}
            label="CPU"
          />
        </div>

        {/* Memory Chart */}
        <div className="bg-brand-black rounded-xl p-4 border border-blue-500/20">
          <div className="flex items-center gap-2 mb-3">
            <MemoryStick className="w-5 h-5 text-blue-400" />
            <span className="text-white font-medium">Utilisation RAM</span>
          </div>
          <LineChart
            data={historyData.memory}
            color={getColorByValue(currentData.memory)}
            height={120}
            label="RAM"
          />
        </div>

        {/* GPU Chart */}
        <div className="bg-brand-black rounded-xl p-4 border border-purple-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Gauge className="w-5 h-5 text-purple-400" />
            <span className="text-white font-medium">Charge GPU</span>
          </div>
          <LineChart
            data={historyData.gpu}
            color={getColorByValue(currentData.gpu)}
            height={120}
            label="GPU"
          />
        </div>

        {/* Disk Chart */}
        <div className="bg-brand-black rounded-xl p-4 border border-yellow-500/20">
          <div className="flex items-center gap-2 mb-3">
            <HardDrive className="w-5 h-5 text-yellow-400" />
            <span className="text-white font-medium">Espace Disque</span>
          </div>
          <LineChart
            data={historyData.disk}
            color={getColorByValue(currentData.disk)}
            height={120}
            label="Disk"
          />
        </div>
      </div>

      {/* Network Charts */}
      <div className="bg-brand-black rounded-xl p-4 border border-cyan-500/20">
        <div className="flex items-center gap-2 mb-4">
          <Network className="w-5 h-5 text-cyan-400" />
          <span className="text-white font-medium">Trafic Reseau</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Download className="w-4 h-4 text-green-400" />
              <span className="text-sm text-gray-400">Download (RX)</span>
              <span className="ml-auto text-green-400 font-bold">{currentData.network_rx.toFixed(1)} MB/s</span>
            </div>
            <BarChart
              data={historyData.network_rx}
              color={{ stroke: '#22c55e', fill: 'rgba(34, 197, 94, 0.3)' }}
              height={80}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Upload className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-gray-400">Upload (TX)</span>
              <span className="ml-auto text-orange-400 font-bold">{currentData.network_tx.toFixed(1)} MB/s</span>
            </div>
            <BarChart
              data={historyData.network_tx}
              color={{ stroke: '#f97316', fill: 'rgba(249, 115, 22, 0.3)' }}
              height={80}
            />
          </div>
        </div>
      </div>

      {/* CPU Histogram */}
      <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-brand-green" />
          <span className="text-white font-medium">Histogramme Charge CPU ({timeRange})</span>
        </div>
        <div className="h-32">
          <BarChart
            data={historyData.cpu}
            height={120}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>Min: {historyData.cpu.length > 0 ? Math.min(...historyData.cpu.map(d => d.value)).toFixed(1) : '0.0'}%</span>
          <span>Max: {historyData.cpu.length > 0 ? Math.max(...historyData.cpu.map(d => d.value)).toFixed(1) : '0.0'}%</span>
          <span>Moy: {historyData.cpu.length > 0 ? (historyData.cpu.reduce((a, b) => a + b.value, 0) / historyData.cpu.length).toFixed(1) : '0.0'}%</span>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
          <div className="text-xs text-gray-500 mb-1">Pic CPU</div>
          <div className="text-xl font-bold text-red-400">
            {historyData.cpu.length > 0 ? Math.max(...historyData.cpu.map(d => d.value)).toFixed(1) : '0.0'}%
          </div>
        </div>
        <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
          <div className="text-xs text-gray-500 mb-1">Pic RAM</div>
          <div className="text-xl font-bold text-blue-400">
            {historyData.memory.length > 0 ? Math.max(...historyData.memory.map(d => d.value)).toFixed(1) : '0.0'}%
          </div>
        </div>
        <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
          <div className="text-xs text-gray-500 mb-1">Pic GPU</div>
          <div className="text-xl font-bold text-purple-400">
            {historyData.gpu.length > 0 ? Math.max(...historyData.gpu.map(d => d.value)).toFixed(1) : '0.0'}%
          </div>
        </div>
        <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
          <div className="text-xs text-gray-500 mb-1">Pic Reseau</div>
          <div className="text-xl font-bold text-cyan-400">
            {historyData.network_rx.length > 0 ? Math.max(...historyData.network_rx.map(d => d.value)).toFixed(1) : '0.0'} MB/s
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCharts;
